import React from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import parse from 'html-react-parser';
import Fancybox from '@@components/Fancybox/Fancybox';
import ImageLoader from '@@components/ImageLoader/ImageLoader';
import GradingReportTables from '@@components/GradingReportTables/GradingReportTables';
import LabGrownBanner from '@@components/LabGrownBanner/LabGrownBanner';
import ReportSupplemental from '@@components/ReportSupplemental/ReportSupplemental';
import ReportTypeHeader from '@@components/ReportTypeHeader/ReportTypeHeader';
import RightRail from '@@components/RightRail/RightRail';
import SupplementalsAccordion from '@@components/SupplementalsAccordion/SupplementalsAccordion';
import SVSAccordion from '@@components/SVSAccordion/SVSAccordion';
import ProvenanceAccordion from '@@components/ProvenanceAccordion/ProvenanceAccordion';
import AGSReport from '@@components/AGSReport/AGSReport';
import { isColoredStoneReport, TLabels, TReportData } from '@@config/report';
import { TSupplementalContent } from '@@config/wcs';

const hasSupplementalContent = (content: TSupplementalContent[] | undefined): boolean =>
    Boolean(content?.some(item => item.BODY_JSON?.length > 0));

type StandardGradingReportProps = {
    reportNumber: string;
    primaryCTA?: URL | undefined;
    data: TReportData;
    labels: TLabels;
};

const StandardGradingReport: React.FC<StandardGradingReportProps> = ({ reportNumber, primaryCTA, data, labels }) => {
    const REPORT_TYPE_CODE = data.REPORT_TYPE_CODE;
    const REPORT_DT_FORMATTED = new Date(data?.REPORT_DATE).toLocaleDateString();
    const CUT_GRADE = data?.CUT_GRADE ?? '';

    // Boolean flags for display logic
    const isNaturalDiamond = ['DD', 'CI', 'CD', 'FC', 'DER', 'DG', 'DOR', 'CDOR'].includes(REPORT_TYPE_CODE);
    const isLabGrown = ['LGDOSS', 'LGDG', 'LGCD', 'LGCI', 'LGDR'].includes(REPORT_TYPE_CODE);
    // Colored stone reports relabel the "Treatment" tab to "Additional Results" (WEB-2025)
    const isColoredStone = isColoredStoneReport(REPORT_TYPE_CODE);

    // Determine if we need to show tabs for supplemental content
    const showTabs: boolean = Boolean(
        data?.['IS_AGS'] === 'TRUE' ||
        hasSupplementalContent(data['COR_DATA']) ||
        hasSupplementalContent(data['GROWTH_METHOD_DATA']) ||
        hasSupplementalContent(data['SVS_DATA']) ||
        hasSupplementalContent(data['TREATMENTS_DATA']) ||
        (hasSupplementalContent(data['TRACR_DATA']) && data?.['TRACR_ID'])
    );

    return (
        <div className='report-details-and-sidebar flex flex-wrap'>
            {isLabGrown && (
                <div className='w-full'>
                    <LabGrownBanner reportTypeCode={REPORT_TYPE_CODE} i18n={labels.legacy} />
                </div>
            )}
            <div className='sm:w-full md:w-3/4'>
                <div className='mb-6 flex flex-wrap'>
                    <div className='flex-1'>
                        <ReportTypeHeader
                            title={data?.['REPORT_TYPE']}
                            subtitle={isNaturalDiamond ? 'For Natural Diamond' : ''}
                        >
                            {data?.['REPORT_SLEEVE_MSG'] && (
                                <p id='REPORT_SLEEVE_MSG' className='report-sleeve-msg mb-0 mt-2 md:pr-5'>
                                    {data['REPORT_SLEEVE_MSG']}
                                </p>
                            )}
                        </ReportTypeHeader>
                    </div>
                    {data?.['DGTIMG'] && (
                        <div className='sm:w-full md:w-1/4'>
                            <div className='mr-0 md:mr-4'>
                                <Fancybox>
                                    <a href={data['DGTIMG']} data-fancybox>
                                        <ImageLoader
                                            className='h-auto max-w-full'
                                            src={data['DGTIMG']}
                                            minHeight={125}
                                            alt='...'
                                        />
                                    </a>
                                </Fancybox>
                            </div>
                        </div>
                    )}
                </div>

                {!showTabs && (
                    <div className='flex flex-wrap'>
                        <div className='w-full'>
                            <GradingReportTables data={data} i18n={labels.legacy} />
                        </div>
                    </div>
                )}

                {showTabs && (
                    <Tabs
                        defaultActiveKey='report-details'
                        className='supp-tabs my-4 flex justify-center border-0 uppercase'
                    >
                        <Tab eventKey='report-details' title='Report Details'>
                            <div className='flex flex-wrap'>
                                <div className='w-full'>
                                    <GradingReportTables data={data} i18n={labels.legacy} />
                                </div>
                            </div>
                        </Tab>

                        {hasSupplementalContent(data['COR_DATA']) && (
                            <Tab eventKey='country-of-origin' title='Country of Origin'>
                                <SupplementalsAccordion supplementalContent={data['COR_DATA']!} />
                            </Tab>
                        )}

                        {hasSupplementalContent(data['TREATMENTS_DATA']) && !data?.['GROWTH_METHOD'] && (
                            <Tab eventKey='treatment' title={isColoredStone ? 'Additional Information' : 'Treatment'}>
                                <SupplementalsAccordion supplementalContent={data['TREATMENTS_DATA']!} />
                            </Tab>
                        )}

                        {hasSupplementalContent(data['GROWTH_METHOD_DATA']) && (
                            <Tab eventKey='growth-method' title='Growth Method'>
                                <SupplementalsAccordion supplementalContent={data['GROWTH_METHOD_DATA']!} />
                            </Tab>
                        )}

                        {hasSupplementalContent(data['SVS_DATA']) && (
                            <Tab
                                eventKey='svs'
                                title={parse(`<span>GIA<sup>®</sup> Source Verification Service</span>`)}
                            >
                                <SVSAccordion
                                    svsName={data?.['SVS_NAME'] ?? ''}
                                    supplementalContent={data['SVS_DATA']!}
                                />
                            </Tab>
                        )}

                        {hasSupplementalContent(data['TRACR_DATA']) && (
                            <Tab eventKey='provenance' title='Provenance'>
                                <ProvenanceAccordion
                                    tracrId={data['TRACR_ID']}
                                    provenanceName={data?.['PROVENANCE_NAME']}
                                    supplementalContent={data['TRACR_DATA']!}
                                />
                            </Tab>
                        )}

                        {data?.['IS_AGS'] === 'TRUE' && (
                            <Tab eventKey='ags' title={parse(`AGS Ideal<sup>&reg;</sup> Report`)}>
                                <AGSReport
                                    REPORT_DT_FORMATTED={REPORT_DT_FORMATTED}
                                    REPORT_NO={reportNumber}
                                    AGS_SHAPE={data.AGS_SHAPE ?? ''}
                                    AGS_MEASUREMENTS={data.AGS_MEASUREMENTS ?? ''}
                                    AGS_CARAT_WEIGHT={data.AGS_CARAT_WEIGHT ?? ''}
                                    AGS_LIGHT_PERFORMANCE_GRADE={data.AGS_LIGHT_PERFORMANCE_GRADE ?? ''}
                                    AGS_BRIGHTNESS={data.AGS_BRIGHTNESS ?? ''}
                                    AGS_FIRE={data.AGS_FIRE ?? ''}
                                    AGS_CONTRAST={data.AGS_CONTRAST ?? ''}
                                    AGS_PDF_URL={data.AGS_PDF_URL ?? ''}
                                    AGS_LIGHTIMG={data.AGS_LIGHTIMG ?? ''}
                                />
                            </Tab>
                        )}
                    </Tabs>
                )}
            </div>
            <div className='my-6 w-full md:hidden'>
                <ReportSupplemental
                    reportTypeCode={REPORT_TYPE_CODE}
                    reportNumber={reportNumber}
                    primaryCTA={primaryCTA}
                    reportDAC={data.DIGITAL_CARD ?? ''}
                    reportDTL={data.DTL_PDF_URL ?? ''}
                    reportDTLP={data.DTLP_PDF_URL ?? ''}
                    reportDTTL={data.DTTL_PDF_URL ?? ''}
                    reportPCL={data.POS_CASE_LABEL ?? ''}
                    i18n={labels.legacy}
                />
            </div>
            <aside id='right-rail' className='sm:w-full md:w-1/4'>
                <RightRail reportTypeCode={REPORT_TYPE_CODE} cutGrade={CUT_GRADE} />
            </aside>
        </div>
    );
};

export default StandardGradingReport;
